import type { HTMLAttributes, ReactNode } from 'react';
import { cx } from '../../lib/util/cx';

/**
 * Port of the DS `StatusPill`: tint fill with full-strength ink — never a
 * saturated fill with white text at pill size (handoff contrast rule).
 * Table rule: a pill column uses a FIXED px track, never `fr`.
 */
export type PillTone = 'success' | 'warning' | 'danger' | 'info' | 'chef' | 'locale' | 'neutral';

const TONE: Record<PillTone, { box: string; dot: string }> = {
  success: { box: 'bg-[rgba(70,162,103,.16)] text-[#35784C]', dot: 'bg-[#35784C]' },
  warning: { box: 'bg-[rgba(180,158,49,.2)] text-[#7A5A12]', dot: 'bg-[#7A5A12]' },
  danger: { box: 'bg-[rgba(178,58,46,.12)] text-[#B23A2E]', dot: 'bg-[#B23A2E]' },
  info: { box: 'bg-[rgba(127,150,254,.2)] text-[#223679]', dot: 'bg-[#223679]' },
  chef: { box: 'bg-[rgba(236,109,45,.16)] text-[#8F4420]', dot: 'bg-[#8F4420]' },
  locale: { box: 'bg-[rgba(124,77,196,.14)] text-[#5B3A94]', dot: 'bg-[#5B3A94]' },
  neutral: { box: 'bg-[rgba(143,68,32,.08)] text-[rgba(42,22,9,.7)]', dot: 'bg-[rgba(42,22,9,.7)]' },
};

export interface StatusPillProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: PillTone;
  dot?: boolean;
  children: ReactNode;
}

export function StatusPill({ tone = 'neutral', dot = false, className, children, ...rest }: StatusPillProps) {
  const t = TONE[tone];
  return (
    <span
      className={cx(
        'inline-flex items-center gap-1.5 whitespace-nowrap rounded-pill px-2.5 py-[3px] font-body text-xs font-semibold',
        t.box,
        className,
      )}
      {...rest}
    >
      {dot && <span aria-hidden="true" className={cx('h-1.5 w-1.5 rounded-full', t.dot)} />}
      {children}
    </span>
  );
}
